/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../../../components/ts-components/Text'
import {
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import MoreOptions from '../../../../components/List/OptionButtons/More'
import OptionButtonDelete from '../../../../components/List/OptionButtons/Delete'
import { formatLongDate } from '@sportninja/common/ts-utils/date-utils'

const CopyIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    height={`${size}px`}
    viewBox='0 -960 960 960'
    width={`${size}px`}
    fill='currentColor'
  >
    <path d='M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-560h80v560h440v80H200Zm160-240v-480 480Z' />
  </svg>
)

type UseCouponColumnsParams = {
  currencyCode?: string
  scheduleId?: string
  organizationId?: string
  onEdit: (record: any) => void
  onDelete: (record: any) => Promise<void>
  onCopy: (record: any) => void
}

export function useCouponColumns({
  scheduleId,
  organizationId,
  onEdit,
  onDelete,
  onCopy,
}: UseCouponColumnsParams) {
  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
          {code}
        </Text>
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      align: 'right' as const,
      render: (d: number, record: any) => (
        <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
          {record.discount_type === 'percentage' ? `${d}%` : `$${d}`}
        </Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'discount_type',
      key: 'discount_type',
      render: (type: string) => (
        <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </Text>
      ),
    },
    {
      title: 'Dates',
      key: 'validity_period',
      render: (_: any, record: any) => {
        if (!record.start_date && !record.end_date) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              -
            </Text>
          )
        }

        const startDate = record.start_date
          ? formatLongDate(record.start_date)
          : null
        const endDate = record.end_date ? formatLongDate(record.end_date) : null

        if (startDate && endDate) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              {`${startDate} - ${endDate}`}
            </Text>
          )
        }

        if (startDate) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              {`From ${startDate}`}
            </Text>
          )
        }

        return (
          <Text variant='paragraphSmall' color={colors.WHITE} weight='regular'>
            {`Until ${endDate}`}
          </Text>
        )
      },
    },
    {
      title: 'Usage',
      dataIndex: 'current_uses',
      key: 'current_uses',
      render: (currentUses: number, record: any) => {
        if (record.max_uses === null || record.max_uses === undefined) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              -
            </Text>
          )
        }

        const isNearLimit = currentUses >= record.max_uses * 0.75

        return (
          <Text
            variant='paragraphSmall'
            color={isNearLimit ? colors.ERROR_200 : colors.WHITE}
            weight='regular'
          >
            {`${currentUses || 0}/${record.max_uses}`}
          </Text>
        )
      },
    },
    {
      title: 'Applies To',
      dataIndex: 'is_deposit',
      key: 'applies_to',
      render: (_: any, record: any) => {
        const isDeposit = record.is_deposit ?? true
        const isInstallment = record.is_installment ?? true

        if (isDeposit && isInstallment) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              Deposit + Payment
            </Text>
          )
        } else if (isDeposit) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              Deposit Only
            </Text>
          )
        } else if (isInstallment) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              Payment Only
            </Text>
          )
        } else {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              -
            </Text>
          )
        }
      },
    },
    {
      title: 'Valid For',
      key: 'valid_for',
      render: (_: any, record: any) => {
        const isTeam = record.is_team ?? false
        const isPlayer = record.is_player ?? true

        if (isTeam && isPlayer) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              Team, Player
            </Text>
          )
        } else if (isTeam) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              Team
            </Text>
          )
        } else if (isPlayer) {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              Player
            </Text>
          )
        } else {
          return (
            <Text
              variant='paragraphSmall'
              color={colors.WHITE}
              weight='regular'
            >
              -
            </Text>
          )
        }
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      align: 'center' as const,
      render: (active: boolean) => (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          {active ? (
            <Tooltip title='Active' placement='top'>
              <CheckCircleOutlined
                style={{ color: colors.SUCCESS_200, fontSize: 20 }}
              />
            </Tooltip>
          ) : (
            <Tooltip title='Inactive' placement='top'>
              <CloseCircleOutlined
                style={{ color: colors.ERROR_200, fontSize: 20 }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      width: 150,
      fixed: 'right' as const,
      render: (_text: string, record: any) => (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: flex-end;
          `}
        >
          <Tooltip title='Copy Code' placement='top'>
            <button
              type='button'
              onClick={() => onCopy(record)}
              css={css`
                background: transparent;
                border: none;
                cursor: pointer;
                color: ${colors.PRIMARY};
                padding: 4px 8px;
                font-size: 18px;
                display: flex;
                align-items: center;
                justify-content: center;
                &:hover {
                  opacity: 0.8;
                }
              `}
            >
              <CopyIcon size={20} />
            </button>
          </Tooltip>
          <button
            type='button'
            onClick={() => onEdit(record)}
            css={css`
              background: transparent;
              border: none;
              cursor: pointer;
              color: ${colors.PRIMARY};
              padding: 4px 8px;
              font-size: 18px;
              display: flex;
              align-items: center;
              justify-content: center;
              &:hover {
                opacity: 0.8;
              }
            `}
            title='Edit Coupon'
          >
            <EditOutlined
              css={css`
                font-size: 20px;
              `}
            />
          </button>
          <MoreOptions
            options={[
              //@ts-ignore
              {
                key: 'delete',
                Component: OptionButtonDelete,
                componentProps: {
                  buttonText: 'delete',
                  title: `Delete ${record.code}`,
                  entityType: 'coupon',
                  itemId: record.id,
                  slug: organizationId
                    ? `/organizations/${organizationId}/coupon/${record.id}`
                    : `/registration/${scheduleId}/admin/coupon/${record.id}`,
                  onComplete: () => onDelete(record),
                  iconName: 'trash',
                  displayBanner: false,
                },
              },
            ]}
          />
        </div>
      ),
    },
  ]

  return columns
}
